import { BREAK_WINDOWS, MAX_CHAIN_MINUTES } from '../variables/shift';

//夜勤(2直)対応の単一計算エンジン。仕様 01-frontend.md §2 に対応。
//calcTotalTimeByDiary(calcTotalTime.ts) と createTimeFixedArray(updateDiaryTime.ts) を置き換える純関数。
//旧2関数はファイルごと残置し、呼出のみ全て本関数へ置換する(リポジトリ慣習)。

export type WorkScheduleEntry = {
  startMin: number; //開始(通算分。1440超え可)
  endMin: number; //終了(通算分)
  startClock: string; //"HH:mm" 巻き戻し(kintone TIME型用) = startMin % 1440
  endClock: string; //"HH:mm" 巻き戻し
  startText: string; //"HH:mm" 24h超え表記(ゼロ埋め) = startMin そのまま
  endText: string; //"HH:mm" 24h超え表記
  spansNextDay: boolean; // endMin > 1440
};

export type WorkSchedule = {
  entries: WorkScheduleEntry[];
  totalMin: number; //作業時間合計(分) ※休憩窓・30分休は含まない
  totalHour: number; //時間換算(小数2位四捨五入) 現行calcTotalTimeByDiaryと同一規約
  totalText: string; //"HH時間MM分"(現行と同一書式)
  endMin: number; //当日終了(通算分)
  endClock: string; //終了の巻き戻し表記
  endText: string; //終了の24h超え表記
  isOverMaxChain: boolean; // endMin - 開始通算分 > MAX_CHAIN_MINUTES
};

const pad2 = (n: number): string => String(n).padStart(2, '0');

//通算分 → "HH:mm" 巻き戻し表記(0時で巻き戻す。kintone TIME型・現行互換用)
const toClock = (min: number): string =>
  `${pad2(Math.floor((min % 1440) / 60))}:${pad2(min % 60)}`;

//通算分 → "HH:mm" 24h超え表記(ゼロ埋めにより文字列ソート=時系列順が成立)
const toText = (min: number): string =>
  `${pad2(Math.floor(min / 60))}:${pad2(min % 60)}`;

//"HH:mm"(timeListの値) → 通算分
const toMinutes = (clock: string): number => {
  const [h, m] = clock.split(':').map(Number);
  return h * 60 + m;
};

//カーソルを含む窓([wStart, wEnd)。なければ undefined)。ループ外定義(no-loop-func回避)。
const windowContaining = (
  windows: Array<[number, number]>,
  cursor: number
): [number, number] | undefined =>
  windows.find(([ws, we]) => ws <= cursor && cursor < we);

//カーソルより後で最初に始まる窓のwStart(なければ∞)。
const nextWindowStart = (
  windows: Array<[number, number]>,
  cursor: number
): number => {
  let next = Infinity;
  for (const [ws] of windows) {
    if (ws > cursor && ws < next) next = ws;
  }
  return next;
};

export const calcWorkSchedule = (
  fromTime: string, // "HH:mm"(timeListの値)
  entries: { minutes: number; isBreak: boolean }[], // diaryListの並び順どおり
  shiftType: number // SHIFT_TYPE_NORMAL | SHIFT_TYPE_NIGHT
): WorkSchedule => {
  //窓は開始日の帯(0–1440分)にのみ存在するため、翌日同時刻帯には自然に適用されない。
  const windows = BREAK_WINDOWS[shiftType] ?? [];
  const fromMin = toMinutes(fromTime);

  let cursor = fromMin;
  let totalMin = 0;

  const scheduleEntries: WorkScheduleEntry[] = entries.map((entry) => {
    const startMin = cursor; //明細開始 = 直前明細の終了(隙間なし)
    totalMin += entry.minutes; //合計は作業分のみ(休憩窓・30分休は含まない)
    let remain = entry.minutes + (entry.isBreak ? 30 : 0); //消費すべきスパン分(30分休は明細内吸収)

    while (remain > 0) {
      //カーソルを含む窓があれば窓末尾へジャンプ(ジャンプ分は明細スパンに吸収)
      const inWindow = windowContaining(windows, cursor);
      if (inWindow) {
        cursor = inWindow[1];
        continue;
      }
      //次の窓開始まで(なければ残量分)進める
      const step = Math.min(remain, nextWindowStart(windows, cursor) - cursor);
      cursor += step;
      remain -= step;
    }

    const endMin = cursor;
    return {
      startMin,
      endMin,
      startClock: toClock(startMin),
      endClock: toClock(endMin),
      startText: toText(startMin),
      endText: toText(endMin),
      spansNextDay: endMin > 1440, //ちょうど24:00(1440)終了は跨ぎなし
    };
  });

  const endMin = cursor;
  const totalHour = Math.round((totalMin / 60) * 100) / 100;
  const totalText = `${pad2(Math.floor(totalHour))}時間${pad2(totalMin % 60)}分`;

  return {
    entries: scheduleEntries,
    totalMin,
    totalHour,
    totalText,
    endMin,
    endClock: toClock(endMin),
    endText: toText(endMin),
    isOverMaxChain: endMin - fromMin > MAX_CHAIN_MINUTES,
  };
};
